import { useApolloClient, useQuery } from '@apollo/client'
import * as Sentry from '@sentry/nextjs'
import { useInterpret, useSelector } from '@xstate/react'
import { getAuth, onAuthStateChanged, onIdTokenChanged } from 'firebase/auth'
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useEffect } from 'react'
import { useEffectOnce } from 'react-use'
import { InterpreterFrom } from 'xstate'
import { MeQuery } from 'types/graphql'
import { setAuthIdCookie } from 'api/authCookies'
import { assertExists } from 'utils/assert'
import { config } from 'utils/config'
import { sleep } from 'utils/sleep'
import { getMeQuery } from 'modules/user/queries/me'
import { useWatchMeQuery } from 'modules/user/queries/useWatchMeQuery'
import { sessionMachine } from '../machines/sessionMachine'
import { useCreateCookieToken } from '../queries/useCreateCookieToken'
import { useServerSideMe } from '../queries/useServerSideMe'

interface Context {
  sessionService: InterpreterFrom<typeof sessionMachine>
  userIsLoaded: () => Promise<boolean>
  levelUpModalVisible: boolean
  setLevelUpModalVisible: (value: boolean) => void
}

export const SessionContext = createContext<Context | null>(null)

interface Props {
  children: React.ReactNode
}
export const SessionProvider = ({ children }: Props) => {
  const client = useApolloClient()
  const { createCookieToken } = useCreateCookieToken()
  const { user } = useServerSideMe()
  const [levelUpModalVisible, setLevelUpModalVisible] = useState<boolean>(false)

  const machine = useMemo(() => sessionMachine(user), [user])

  const sessionService = useInterpret(machine, {
    devTools: config.apiEnv !== 'production',
    services: {
      getCurrentUser: async (): Promise<MeQuery['me']> => {
        const tryToGetCurrentUser = async (iteration = 1): Promise<MeQuery['me']> => {
          Sentry.addBreadcrumb({
            category: 'getCurrentUser',
            message: `Iteration ${iteration}`
          })

          try {
            const { data, error } = await client.query<MeQuery>({ query: getMeQuery })

            Sentry.addBreadcrumb({
              category: 'getCurrentUser',
              message: `Iteration ${iteration} ID: ${data?.me?.id}, Me Present: ${!!data?.me}, Error: ${error?.message}`
            })

            if (error) {
              Sentry.withScope((scope) => {
                scope.setTag('auth-error', 'true')
                Sentry.captureException(error)
              })
              throw new Error('Failed to fetch user')
            }

            return data.me
          } catch (err: any) {
            Sentry.addBreadcrumb({
              category: 'getCurrentUser',
              message: `Iteration ${iteration} failed; ${err.message}`
            })
            if (iteration < 5) {
              await sleep(iteration * 100)
              return tryToGetCurrentUser(iteration + 1)
            } else {
              throw new Error('Failed to fetch user')
            }
          }
        }

        return tryToGetCurrentUser()
      }
    }
  })

  const isLoggedIn = useSelector(sessionService, (state) => state.matches('loggedIn'))

  // Poll current user periodically to keep data up-to-date
  useQuery<MeQuery>(getMeQuery, {
    skip: !isLoggedIn,
    pollInterval: 30000
  })

  const { send } = sessionService

  useWatchMeQuery((me) => {
    if (me) {
      send({ type: 'UPDATE_USER', user: me })
    }
  })

  useEffectOnce(() => {
    const auth = getAuth()
    const authIdListenerUnsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser && isLoggedIn) {
        await setAuthIdCookie(firebaseUser)
        createCookieToken()
      }
    })

    return authIdListenerUnsubscribe
  })

  useEffect(() => {
    const auth = getAuth()
    onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        send({ type: 'FETCH_USER' })
      } else {
        send({ type: 'SET_NO_USER' })
      }
    })
  }, [send])

  /**
   * This is a workaround to allow the AuthenticationProvider to wait for the current user to be loaded
   * instead of trying to read from the session service directly.
   */
  const userIsLoaded = useCallback(async (): Promise<boolean> => {
    const res = client.cache.readQuery<MeQuery>({ query: getMeQuery })
    return !!res?.me
  }, [client.cache])
  return (
    <SessionContext.Provider value={{ sessionService, userIsLoaded, levelUpModalVisible, setLevelUpModalVisible }}>
      {children}
    </SessionContext.Provider>
  )
}

export const useSessionContext = () => {
  const ctx = useContext(SessionContext)
  assertExists(ctx)
  return ctx
}
